import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../lib/session/session-context.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useConfirm,
} from '@tailoring-crm/ui';

/**
 * The account menu. It shows who is signed in and in what role, because a shop's tablet is
 * shared — the manager and two tailors all use it, and knowing whose session is open decides
 * whether the payment button is even there.
 *
 * Signing out is wired to a callback rather than to an endpoint: the endpoint and the login
 * screen are task 16's.
 */
export function UserMenu({ onSignOut }: { onSignOut?: () => void }) {
  const { t } = useTranslation();
  const session = useSession();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const initials = session ? initialsOf(session.userName) : null;

  const handleSignOut = async () => {
    const ok = await confirm({
      title: t('account.signOutTitle'),
      description: t('account.signOutDescription'),
      confirmLabel: t('account.signOut'),
    });
    if (ok) onSignOut?.();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('account.menuLabel')}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-sm font-semibold text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent-foreground/60"
      >
        {initials ?? <UserRound aria-hidden="true" className="size-5" />}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          <span className="block">{session?.userName ?? t('account.noSession')}</span>
          <span className="block text-xs font-normal text-muted-foreground">
            {session ? t(`role.${session.role}`) : t('account.noSessionHint')}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void navigate('/settings');
          }}
        >
          <Settings aria-hidden="true" />
          {t('nav.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem
          destructive
          disabled={!session}
          onSelect={(event) => {
            // Keep the menu's focus restoration from racing the confirmation dialog.
            event.preventDefault();
            void handleSignOut();
          }}
        >
          <LogOut aria-hidden="true" />
          {t('account.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
